import React, { useState } from 'react';
import { NameEmailAvatar } from '../../../../../../../ui/components/Common/NameEmailAvatar';
import { WellnessCandidate } from '../../../../../../../utils/types/wellness_types';
import { DataTable, TableColumn } from '../../../../../../../ui'; // adjust path to match your project

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  onPageChange: (p: number) => void;
}

interface CandidateTabProps {
  jobOpeningId: string;
  candidates: WellnessCandidate[];
  isLoading: boolean;
  onSendInvitation: (candidateIds: string[]) => void;
  onSendReminder: (candidateId: string) => void;
  onViewResults: () => void;
  pagination: PaginationProps;
  search: string;
  onSearchChange: (v: string) => void;
  emailSearch: string;
  onEmailSearchChange: (v: string) => void;
  assessmentStatusFilter: string;
  onAssessmentStatusFilterChange: (v: string) => void;
}

const downloadCsv = (candidates: WellnessCandidate[]) => {
  const header = ['Name', 'Email', 'Status', 'Invited', 'Assessment Status', 'Invitation Count'];
  const rows = candidates.map((c) => [
    c.name, c.email, c.applicantStatus, c.invited ? 'YES' : 'NO', c.assessmentStatus, c.invitationCount,
  ]);
  const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'wellness_candidates.csv'; a.click();
  URL.revokeObjectURL(url);
};

const TooltipWrapper: React.FC<{ text: string }> = ({ text }) => {
  return (
      <div className='relative group w-full flex items-center justify-start text-left'>
        <div className='max-w-[180px] truncate whitespace-nowrap overflow-hidden'>
          {text || '-'}
        </div>
        <div className='absolute z-[9999] hidden group-hover:flex flex-col items-center'>
          <div className='relative'>
            <div className='absolute bottom-full px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg w-max max-w-xs whitespace-normal'>
              {text}
            </div>
          </div>
        </div>
      </div>
  );
};

const formatStatusLabel = (value: string): string => {
  if (!value) return '';
  return value
      .split(/[-_]/)
      .map((part) =>
          part
              .replace(/Inprogress/gi, 'In Progress')
              .replace(/([a-z])([A-Z])/g, '$1 $2'),
      )
      .join(' ')
      .trim();
};

export const CandidateTab: React.FC<CandidateTabProps> = ({
                                                            candidates,
                                                            isLoading,
                                                            onSendInvitation,
                                                            pagination,
                                                            search,
                                                            onSearchChange,
                                                            emailSearch,
                                                            onEmailSearchChange,
                                                            assessmentStatusFilter,
                                                            onAssessmentStatusFilterChange,
                                                          }) => {
  const [statusFilter, setStatusFilter] = useState('Wellness');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = (id: string) =>
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });

  const toggleAll = () =>
      setSelectedIds((prev) =>
          prev.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.candidateId)),
      );

  const allSelected = candidates.length > 0 && selectedIds.size === candidates.length;
  const someSelected = selectedIds.size > 0;
  const { currentPage, totalPages, totalRecords, onPageChange } = pagination;

  const columns: TableColumn<WellnessCandidate>[] = [
    {
      key: 'candidate',
      label: 'CANDIDATE',
      width: '30%',
      align: 'left',
      disableSort: true,
      render: (row) => (
          <NameEmailAvatar
              name={row.name}
              email={row.email}
              avatar={undefined}
              avatarWidth='32px'
              avatarHeight='32px'
              nameFontSize='13px'
              emailFontSize='11px'
          />
      ),
    },
    {
      key: 'status',
      label: 'STATUS',
      width: '20%',
      align: 'left',
      disableSort: true,
      render: (row) => <TooltipWrapper text={formatStatusLabel(row.applicantStatus)} />,
    },
    {
      key: 'invited',
      label: 'INVITED',
      width: '13%',
      align: 'left',
      disableSort: true,
      render: (row) => <TooltipWrapper text={row.invited ? 'YES' : 'NO'} />,
    },
    {
      key: 'invitationCount',
      label: 'INVITATIONS',
      width: '12%',
      align: 'left',
      disableSort: true,
      render: (row) => <TooltipWrapper text={String(row.invitationCount)} />,
    },
    {
      key: 'assessmentStatus',
      label: 'ASSESSMENT STATUS',
      width: '25%',
      align: 'left',
      disableSort: true,
      render: (row) => (
          <TooltipWrapper text={row.assessmentStatus === '-' ? '-' : formatStatusLabel(row.assessmentStatus)} />
      ),
    },
  ];

  return (
      <div className='space-y-3'>

        {/* ── Filter row ──────────────────────────────────────────────── */}
        <div className='flex flex-wrap items-center gap-2'>

          {/* Name Search */}
          <div className='relative min-w-[160px] flex-1'>
            <svg className='pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
              <circle cx='11' cy='11' r='8' /><path d='m21 21-4.35-4.35' strokeLinecap='round' />
            </svg>
            <input
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder='Search by name...'
                className='w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40'
            />
          </div>

          {/* Email Search */}
          <div className='relative min-w-[160px] flex-1'>
            <svg className='pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
              <circle cx='11' cy='11' r='8' /><path d='m21 21-4.35-4.35' strokeLinecap='round' />
            </svg>
            <input
                value={emailSearch}
                onChange={(e) => onEmailSearchChange(e.target.value)}
                placeholder='Search by email...'
                className='w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40'
            />
          </div>

          {/* Applicant Status */}
          <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className='rounded-lg border border-slate-200 bg-white py-2 pl-3 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 appearance-none min-w-[140px]'
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1rem' }}
          >
            <option value='Wellness'>Wellness</option>
            <option value='All'>All Statuses</option>
          </select>

          {/* Assessment status */}
          <select
              value={assessmentStatusFilter}
              onChange={(e) => onAssessmentStatusFilterChange(e.target.value)}
              className='rounded-lg border border-slate-200 bg-white py-2 pl-3 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 appearance-none min-w-[170px]'
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1rem' }}
          >
            <option value=''>-- All Assessments --</option>
            <option value='Assigned'>Not Invited</option>
            <option value='Invite Sent'>Invite Sent</option>
            <option value='In Progress'>In Progress</option>
            <option value='Completed'>Completed</option>
            <option value='Expired'>Expired</option>
          </select>

          <div className='ml-auto flex items-center gap-2'>
            <button
                type='button'
                onClick={() => downloadCsv(candidates)}
                className='inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors'
            >
              Export To CSV
            </button>
            <button
                type='button'
                disabled={!someSelected}
                className='inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-400 hover:text-slate-600 disabled:cursor-not-allowed transition-colors'
            >
              Remove Invite
            </button>
          </div>
        </div>

        {/* ── Bulk action bar ─────────────────────────────────────────── */}
        {someSelected && (
            <div className='flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-2'>
              <span className='text-sm font-semibold text-emerald-800'>{selectedIds.size} selected</span>
              <button
                  type='button'
                  onClick={() => { onSendInvitation(Array.from(selectedIds)); setSelectedIds(new Set()); }}
                  className='rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors'
              >
                Send Invitation ({selectedIds.size})
              </button>
              <button type='button' onClick={() => setSelectedIds(new Set())} className='ml-auto text-xs text-slate-500 hover:text-slate-700'>
                Clear
              </button>
            </div>
        )}

        {/* ── Table ───────────────────────────────────────────────────── */}
        <DataTable<WellnessCandidate>
            isLoading={isLoading}
            columns={columns}
            data={candidates}
            rowsPerPage={20}
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
            hasCheckBox
            onSelectAll={toggleAll}
            onSelectRow={(_rowIndex, row) => toggleSelect(row.candidateId)}
            isRowSelected={(_rowIndex, row) => selectedIds.has(row.candidateId)}
            isAllSelected={allSelected}
            isIndeterminate={someSelected && !allSelected}
        />
      </div>
  );
};